import type { Problem, ProblemKind, Received } from '@norscom/nap-shared';

import type { ValidationWarning } from './types';

/**
 * How badly one path went wrong. The distinction is what a message can be
 * built from:
 *
 * - `drift`      — a scalar was off, or a list arrived as something that is not
 *                  a list; the parser substituted a default and parsing
 *                  continued. The value is usable but this field is not what
 *                  the server sent.
 * - `dropped`    — rows the parser could not read were removed from a list. The
 *                  value is usable but shorter than the response, and `dropped`
 *                  says by how much.
 * - `unreadable` — recorded on a response that failed *structurally*: the
 *                  container was the wrong kind, and there was nothing to
 *                  substitute. A `REJECT` field that had to substitute reaches
 *                  here too, through the object it sank.
 */
export type { ProblemKind };

/**
 * One thing that went wrong at the boundary, machine-readable.
 *
 * NOTE: deliberately carries no user-facing text. An endpoint does not know
 * whether it is serving a dialog or a background poller, so it is in no
 * position to word a message — the caller does that. Mirrors
 * `ValidationWarning`'s fields (readonly, plus `kind`) rather than extending
 * it, so a `Problem` can also describe a structural failure, which has no
 * `usedDefault`.
 *
 * `dropped` carries its count on the same arm, so a consumer that narrows to
 * the kind gets the numbers. That is also why a non-array substituted to `[]`
 * is `drift` and not `dropped` — no rows were read, so there is no total.
 *
 * The count is not optional: the only thing that stamps `dropped` is the parser
 * that counted what it removed, so a `dropped` without numbers is
 * unrepresentable rather than merely unusual.
 *
 * `source` says which call received it — `runSchema`'s `source` label,
 * conventionally the endpoint's own name. Optional because `enforce` classifies
 * warnings it was handed without being told where they came from; the parse
 * helpers that DO know stamp it, so a message can name the request instead of
 * leaving the user with an unattributed "the response could not be read".
 */
export type { Problem };

/** Build a `Problem` of the given kind from a parser warning. */
export const problemOf = (warning: ValidationWarning, kind: ProblemKind, source?: string): Problem => {
  const base = {
    path: warning.path,
    expected: warning.expected,
    received: warning.received,
    usedDefault: warning.usedDefault,
    ...(warning.notes && warning.notes.length > 0 ? { notes: warning.notes } : {}),
    ...(source === undefined ? {} : { source }),
  };
  // The count is the evidence for the kind, so the parser that counted decides
  // it — `kind` is only the classification for everything else.
  if (warning.dropped !== undefined) return { ...base, kind: 'dropped', dropped: warning.dropped };
  return { ...base, kind: kind === 'dropped' ? 'drift' : kind };
};

/** Classify every warning from a schema run the same way. */
export const problemsOf = (warnings: ReadonlyMap<string, ValidationWarning>, kind: ProblemKind, source?: string): readonly Problem[] =>
  Array.from(warnings.values(), (w) => problemOf(w, kind, source));

/**
 * What came back from the boundary — the *state* channel (issue #1035).
 *
 * A failed API call is an **error**: no answer arrived (transport, 5xx, auth)
 * or the server refused the input (400). Those reject, because each has
 * exactly one handling. `Received` is the other channel: an answer *did*
 * arrive, and this says what it was.
 *
 * Three arms, and each property of the shape is load-bearing (issue #1220):
 *
 * - **`ok` carries no `problems` field at all**, so "usable with no
 *   reservations" is a type rather than a `problems.length === 0` check every
 *   caller has to remember to write.
 * - **The value is renamed on the degraded arm.** Reaching for data you have
 *   been told is degraded means typing `degradedValue`, every time. That does
 *   not *prevent* a consumer from ignoring `problems` — nothing can — but it
 *   makes doing so something someone wrote, and `grep -rn degradedValue` the
 *   list of everywhere it happened. Same instrument as `acceptDegraded` below.
 * - **`unreadable` has no value at all**, so a caller cannot reach one that
 *   failed validation, and forgetting the case is a compile error.
 * - **The arms are not graded.** A dropped row is worse than a defaulted
 *   label, and that difference lives in `problems[].kind` for a consumer that
 *   wants it. Severity must not move onto the arm: an arm that means "bad
 *   enough to refuse" is the endpoint deciding for the consumer again.
 *
 * For a by-id read this yields four outcomes with no extra machinery:
 * `Received<Lock | null>`, where an `ok` value of `null` is *the server
 * answered: no such record* (a 404 is an answer, not a failed call).
 *
 * This REVERSES the reasoning this file used to carry, which argued that
 * "usable but with reservations" should not be an arm because "every caller
 * would branch on it identically to `ok`". That was true while substitution
 * was silent and nothing read `problems` — the control flow genuinely was the
 * same. It is false now: an editor must refuse degraded, a viewer may accept
 * it, and a list wants to say how much it lost. The premise expired, so the
 * conclusion went with it.
 *
 * `Problem`, `ProblemKind` and `Received` are declared in `@norscom/nap-shared`,
 * so that its degrading `read` returns exactly this type: one declaration, which
 * a package version that changes it breaks at compile time.
 */
export type { Received };

/** The clean arm: an answer we could read with no reservations. */
export const received = <T>(value: T): Received<T> => ({ result: 'ok', value });

/** The qualified arm: an answer we could read, but not all of it. */
export const degraded = <T>(degradedValue: T, problems: readonly Problem[]): Received<T> => ({ result: 'degraded', degradedValue, problems });

/** The failure arm: an answer arrived and we could not read it. */
export const unreadable = <T>(problems: readonly Problem[] = []): Received<T> => ({ result: 'unreadable', problems });

/**
 * Map the value an answer carries, keeping the arm and the problems.
 *
 * For an endpoint or operation that reshapes what it read — projecting one
 * field out of an envelope, mapping wire rows to view rows. Collapsing first
 * and re-wrapping would throw away what it cost to read; narrowing by hand at
 * every such site would be three branches each.
 */
export const mapReceived = <T, U>(answer: Received<T>, fn: (value: T) => U): Received<U> => {
  if (answer.result === 'ok') return received(fn(answer.value));
  if (answer.result === 'degraded') return degraded(fn(answer.degradedValue), answer.problems);
  return answer;
};

/**
 * Collapse a `Received` into its value, throwing unless the answer is **whole**.
 *
 * The strict sibling of `acceptDegraded`, for a consumer that cannot use a partial
 * answer at all. The case that motivates it is a picker: a dropped row is an
 * option the user cannot choose, and one they had chosen before no longer
 * resolves — so a list quietly short by one row does not degrade the control,
 * it breaks it, and silently. An error state at least says so.
 *
 * Prefer `acceptDegraded` wherever a partial answer is still worth showing;
 * reach for this where the whole set IS the answer.
 *
 * NOTE: it deliberately refuses ALL drift, not just dropped rows. A picker
 * cannot know which substituted field its labels or keys are built from, and
 * the alternative — naming the paths it depends on — is the per-path
 * declaration #1220 removed.
 */
export const refuseDegraded = <T>(result: Received<T>): T => {
  if (result.result === 'ok') return result.value;
  const source = result.problems.find((p) => p.source !== undefined)?.source;
  const what = result.result === 'degraded' ? 'the response could not be read in full' : 'the response could not be read';
  throw new Error(source === undefined ? what : `${source}: ${what}`);
};

/**
 * On the third collapse that does not exist, so that nobody builds two.
 *
 * Between "any partial answer will do" and "nothing but the whole thing" sits a
 * consumer that depends on SOME of what it read. The tempting shape is a paths
 * list — refuse if a problem touches one of these — and it is the wrong tool for
 * the reason the per-endpoint `critical` list was: **a path that matches nothing
 * protects nothing and says so nowhere.** Every such declaration on this branch
 * was eventually found inert, twice by review rather than by the type system.
 *
 * Scope the READ instead. A contract that declares only the fields a consumer
 * uses cannot warn about anything else, so `refuseDegraded` over it already
 * means "refuse on what I depend on" — with no paths to keep in step, and no way
 * to be inert, because an undeclared field cannot be read and `tsc` says so. See
 * the projection contracts in `../api/README.md`.
 *
 * If one is ever genuinely needed, invert it: "strict on everything EXCEPT these"
 * fails safe when its list matches nothing, where "strict on these" fails silent.
 * No consumer in the tree wanted it when this was written — the pickers, which
 * looked like the case for it, were better served by a narrower read.
 */

/**
 * Collapse a `Received` into its value, taking a **degraded** answer as well as
 * a clean one, and throwing only when there is no value at all.
 *
 * The name states the only thing worth stating. `unreadable` always throws and
 * `ok` never does, so the sole decision a caller makes here is what to do about
 * the arm in between — and taking it is a choice, not a default. Throwing on
 * degraded instead would kill a monitoring view over one cosmetic drift, which
 * is the opposite of what substituting-and-reporting is for; where that is the
 * wrong trade, use `refuseDegraded`.
 *
 * This is the opt-out for a caller whose handling of an unreadable response is
 * the same as its handling of a failed request — a read whose only consumer
 * displays it, where "we couldn't get you this list" is accurate either way. It
 * is deliberately a named call at the call site rather than a throw hidden
 * inside a combinator, so that **the collapse is greppable**: the call sites are
 * the inventory of responses someone decided have a single handling, AND of the
 * places that accepted partial data. It is what to revisit when one of them
 * grows an editor.
 *
 * Do NOT use it where the response is a write's receipt, or the record an
 * editor is about to open on. There the disagreement between client and
 * server is about state the user has already changed, and it needs its own
 * outcome.
 *
 * The message names the call when a problem carries a `source`: without it the
 * user is told "the response could not be read" with nothing saying which
 * response, which is what the per-endpoint throws used to supply.
 */
export const acceptDegraded = <T>(result: Received<T>): T => {
  if (result.result === 'ok') return result.value;
  if (result.result === 'degraded') return result.degradedValue;
  const source = result.problems.find((p) => p.source !== undefined)?.source;
  throw new Error(source === undefined ? 'the response could not be read' : `${source}: the response could not be read`);
};
